import { FC, useEffect, useState } from 'react';
import { NextgenImage, FAIcon, useTranslation, IconStyle, LoaderType, useConfig } from '@nextgen-web-framework/all';
import type { IconName } from '@fortawesome/pro-solid-svg-icons';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import styles from './ImageComponent.module.scss';
import { ImageType } from './types/enums';
import { ImageComponentPropsTypes, ImageDynamicPropType } from './types/propsTypes';

const ImageComponent: FC<ImageComponentPropsTypes> = ({
  link,
  alt,
  imagetype,
}: ImageComponentPropsTypes): JSX.Element => {
  const t = useTranslation();
  const configs = useConfig();
  const [iconName, setIconName] = useState<IconName | undefined>(undefined);
  const [imgLoading, setImgLoading] = useState(true);

  useEffect(() => {
    setIconName(!link ? 'image' : undefined);
  }, [link]);

  const getImageConfig = (): ImageDynamicPropType => {
    switch (imagetype) {
      case ImageType.PRODUCT_GALLERY_IMAGE_THUMBNAIL:
        return {
          className: styles['imageComp-product-gallery-thumbnail'],
          quality: 40,
        };
      case ImageType.PRODUCT_GALLERY_IMAGE_MODAL_THUMBNAIL:
        return {
          className: styles['imageComp-product-gallery-modal-thumbnail'],
        };
      case ImageType.PLPHEADERCARD:
        return {
          className: styles['imageComp-plp-header'],
        };
      case ImageType.PRODUCT_INGREDIENT:
        return {
          className: styles['imageComp-product-ingredient'],
          quality: 100,
        };
      case ImageType.PRODUCT_GALLERY_IMAGE:
        return {
          className: styles['imageComp-product-gallery'],
          quality: 100,
          priority: true,
          loading: 'eager',
        };
      case ImageType.PRODUCT_GALLERY_IMAGE_MODAL:
        return {
          className: styles['imageComp-product-gallery-modal'],
          quality: 100,
          priority: true,
          loading: 'eager',
        };
      case ImageType.PLP_SEARCH_CARD:
        return {
          className: styles['imageComp-product-plp-search-card'],
        };
      case ImageType.PLP_LISTING_CONTAINER:
        return {
          className: styles['imageComp-product-plp-listing-container'],
          quality: 100,
        };
      case ImageType.CHECKOUT_CARD:
        return {
          className: styles['imageComp-checkout-card'],
        };
      case ImageType.APOINT_REDEEM_CARD:
        return {
          className: styles['imageComp-apoint-redeem-card'],
        };
      case ImageType.CART_CARD:
        return {
          className: styles['imageComp-cart-card'],
        };
      case ImageType.HORIZONTAL_CARD:
        return {
          className: styles['imageComp-horizontal-card'],
        };
      case ImageType.SEARCH_RESULT_CARD:
        return {
          className: styles['imageComp-search-result-card'],
        };
      case ImageType.PRODUCT_DETAILS:
        return {
          className: styles['imageComp-product-details'],
          fill: false,
          height: Number.MAX_SAFE_INTEGER,
          width: Number.MAX_SAFE_INTEGER,
        };
      default:
        return {
          className: styles['image-default'],
        };
    }
  };

  const commonProps = () => ({
    src: link,
    alt: alt || t('product.image.noImageAvailable'),
    'data-testid': 'image-comp',
    'object-fit': 'cover',
    quality: 60,
    onError: () => setIconName('image-slash'),
    ...getImageConfig(),
    loaderType: link.includes('contentstack') ? LoaderType.CONTENTSTACK : LoaderType.WEBFONTS, // check image source is content stack or not
    forceCdnUrlConversion: link.includes(configs.NEXT_PUBLIC_CONTENT_STACK_CDN_URL as string), // check if image source is cdn.contentstack or not
  });

  const noImageSection = (
    <div className={`${getImageConfig().className} ${styles['no-image-container']}`}>
      <FAIcon iconName={iconName as IconName} className={styles['no-image-icon']} iconStyle={IconStyle.REGULAR} />
    </div>
  );

  const noImageContainer = (
    <>
      {/* Case for No Image */}
      {!link && iconName && noImageSection}
      {/* Case for Image Load failure */}
      {link && iconName && noImageSection}
    </>
  );

  return (
    <>
      {[ImageType.PRODUCT_GALLERY_IMAGE, ImageType.PRODUCT_GALLERY_IMAGE_MODAL].includes(imagetype as ImageType) ? (
        <>
          {link && !iconName && <NextgenImage {...commonProps()} onLoad={() => setImgLoading(false)} />}
          {imgLoading && <SkeletonLoader className={getImageConfig().className} />}
          {noImageContainer}
        </>
      ) : (
        <>
          {link && !iconName && <NextgenImage {...commonProps()} />}
          {noImageContainer}
        </>
      )}
    </>
  );
};

export default ImageComponent;
export { ImageType };
export type { ImageComponentPropsTypes };
